// components/dashboard/EditStagesDialog.jsx

import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api from "@/api/axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, Trash2, GripVertical } from "lucide-react";
import { Skeleton } from "../ui/skeleton";

// dnd-kit imports
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Helper to generate unique IDs for stages
const genId = (() => {
  let counter = 0;
  return () => `stage-${Date.now()}-${counter++}`;
})();

// Sortable Item
function SortableStage({ stage, index, onChange, onRemove, totalCost }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: stage.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const fee_percentage =
    totalCost > 0 ? ((stage.fee_amount || 0) / totalCost) * 100 : 0;

  const handleAmountChange = (value) => {
    const feeAmount = parseFloat(value) || 0;
    onChange(stage.id, "fee_amount", feeAmount);
  };

  const handlePercentageChange = (value) => {
    const percentage = parseFloat(value) || 0;
    const feeAmount = totalCost * (percentage / 100);
    onChange(stage.id, "fee_amount", feeAmount);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-3 border rounded-lg bg-muted/50"
    >
      <div {...attributes} {...listeners} className="cursor-grab">
        <GripVertical className="h-5 w-5 text-muted-foreground flex-shrink-0" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-grow">
        <div className="grid items-center">
          <Label>Stage {index + 1} Name</Label>
          <Input
            value={stage.name}
            onChange={(e) => onChange(stage.id, "name", e.target.value)}
          />
        </div>
        <div className="flex gap-4">
          <div className="grid items-center flex-1">
            <Label>Fee Amount</Label>
            <Input
              type="number"
            //   value={stage.fee_amount?.toFixed(2) || 0}
              value={stage.fee_amount || 0}
              onChange={(e) => handleAmountChange(e.target.value)}
            />
          </div>
          <div className="grid items-center   ">
            <Label className="whitespace-nowrap">Fee Percentage (%)</Label>
            <Input
              type="number"
              value={fee_percentage.toFixed(2)}
              className="w-24"
              onChange={(e) => handlePercentageChange(e.target.value)}
            />
          </div>
        </div>
      </div>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            className="text-destructive flex-shrink-0"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this stage?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the stage "
              {stage.name || `Stage ${index + 1}`}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => onRemove(stage.id)}>
              Yes, Delete Stage
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function EditStagesDialog({
  projectId,
  projectName,
  totalCost = 0,
  onSaved,
}) {
  const [open, setOpen] = useState(false);
  const [stages, setStages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  const fetchStages = useCallback(async () => {
    if (!projectId) return;
    setIsLoading(true);
    try {
      const response = await api.get(`${API}/projects/${projectId}/stages`);
      const loadedStages = (response.data || []).map((stage) => ({
        ...stage,
        id: stage.id || genId(), // Ensure unique IDs
        fee_amount: stage.fee_amount || 0,
      }));
      setStages(loadedStages);
    } catch (error) {
      toast.error("Failed to fetch project stages.");
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open) fetchStages();
  }, [open, fetchStages]);

  const handleStageChange = (id, field, value) => {
    setStages((prev) =>
      prev.map((stage) =>
        stage.id === id ? { ...stage, [field]: value } : stage,
      ),
    );
  };

  const handleAddStage = () => {
    setStages([...stages, { id: genId(), name: "", fee_amount: 0 }]);
  };

  const handleRemoveStage = (id) => {
    setStages(stages.filter((s) => s.id !== id));
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = stages.findIndex((s) => s.id === active.id);
    const newIndex = stages.findIndex((s) => s.id === over.id);
    setStages((items) => arrayMove(items, oldIndex, newIndex));
  };

  const handleSaveChanges = async () => {
    // const totalPercentage = stages.reduce((sum, s) => sum + ((s.fee_amount || 0) / totalCost) * 100, 0);
    // if (totalPercentage > 100.001) {
    //     toast.error("Total percentage exceeds 100%. Adjust amounts.");
    //     return;
    // }

    let totalPercentage = 0;

    if (totalCost > 0) {
      totalPercentage = stages.reduce(
        (sum, s) => sum + ((s.fee_amount || 0) / totalCost) * 100,
        0,
      );

      if (totalPercentage > 100.001) {
        toast.error("Total percentage exceeds 100%. Adjust amounts.");
        return;
      }
    }

    setIsSaving(true);
    try {
      await api.post(`${API}/projects/${projectId}/stages`, {
        stages: stages.map(({ id, ...rest }) => rest), // Strip UI IDs
      });
      toast.success("Project stages saved successfully!");
      onSaved();
      setOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save stages.");
    } finally {
      setIsSaving(false);
    }
  };

//   const totalPercentage = stages.reduce(
//     (sum, s) => sum + ((s.fee_amount || 0) / totalCost) * 100,
//     0,
//   );

const totalPercentage =
    totalCost > 0
        ? stages.reduce(
              (sum, s) => sum + ((s.fee_amount || 0) / totalCost) * 100,
              0
          )
        : 0;


  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          Edit Stages
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-6xl">
        <DialogHeader>
          <DialogTitle>Edit Stages for "{projectName}"</DialogTitle>
          <DialogDescription>
            Total project cost is ₹{totalCost.toFixed(2)}. Percentages
            auto-calculate.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 max-h-[60vh] overflow-y-auto pr-2 space-y-4">
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={stages.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                {stages.map((stage, index) => (
                  <SortableStage
                    key={stage.id}
                    stage={stage}
                    index={index}
                    totalCost={totalCost}
                    onChange={handleStageChange}
                    onRemove={handleRemoveStage}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
          <Button variant="outline" className="w-full" onClick={handleAddStage}>
            <Plus className="mr-2 h-4 w-4" />
            Add New Stage
          </Button>
        </div>

        <div className="flex justify-between mt-4 text-sm font-medium">
          <span>Total Percentage:</span>
          <span className={totalPercentage > 100 ? "text-red-500" : ""}>
            {totalPercentage.toFixed(2)}%
          </span>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={handleSaveChanges} disabled={isSaving || isLoading}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
